import { normalisePath, type FileBundle, type FileHandle } from "./file-bundle.js";

/** A file paired with its explicit relative path within the folder bundle. */
export interface PathedFile {
  path: string;
  file: File;
}

/**
 * Build a FileBundle from a browser `FileList` produced by a
 * `<input type="file" webkitdirectory>` picker or by iterating
 * `DataTransferItem.webkitGetAsEntry()` results from a drag-and-drop.
 *
 * The input may be either bare `File` objects (in which case each File's
 * `webkitRelativePath` is used, falling back to `file.name`), or explicit
 * `{ path, file }` pairs. The pair form is required when the files cross
 * a `postMessage` boundary to a Web Worker, because `File.webkitRelativePath`
 * is a non-writable getter that is NOT preserved by structured cloning.
 */
export function folderSourceFromFiles(
  input: Iterable<File | PathedFile>,
): FileBundle {
  const handles: FileHandle[] = [];
  const byPath = new Map<string, File>();
  for (const entry of input) {
    const { path, file } = toPathedFile(entry);
    byPath.set(path, file);
    handles.push({ path, name: basename(path), size: file.size });
  }
  handles.sort((a, b) => a.path.localeCompare(b.path));

  const get = (path: string): File => {
    const f = byPath.get(path);
    if (!f) throw new Error(`File not found in folder drop: ${path}`);
    return f;
  };

  return {
    files: handles,
    async readText(path) {
      return await get(path).text();
    },
    streamText(path) {
      return streamFileAsText(get(path));
    },
  };
}

function toPathedFile(entry: File | PathedFile): { path: string; file: File } {
  if ("file" in entry && entry.file instanceof File) {
    return { path: normalisePath(entry.path), file: entry.file };
  }
  const file = entry as File;
  const raw =
    (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
  return { path: normalisePath(raw), file };
}

/**
 * Capture the effective relative path of each File into an explicit
 * `{ path, file }` pair. Call this before posting files to a Web Worker so
 * the path survives structured cloning (which would otherwise drop
 * `webkitRelativePath`).
 */
export function pathedFilesFromFileList(files: Iterable<File>): PathedFile[] {
  const out: PathedFile[] = [];
  for (const file of files) {
    const raw =
      (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
    out.push({ path: normalisePath(raw), file });
  }
  return out;
}

function basename(path: string): string {
  const i = path.lastIndexOf("/");
  return i === -1 ? path : path.slice(i + 1);
}

async function* streamFileAsText(file: File): AsyncGenerator<string> {
  const stream = file.stream();
  const reader = stream.getReader();
  const decoder = new TextDecoder("utf-8");
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      if (value && value.length) yield decoder.decode(value, { stream: true });
    }
    const tail = decoder.decode();
    if (tail.length) yield tail;
  } finally {
    reader.releaseLock();
  }
}

/**
 * Walk a `DataTransferItemList` (drag-and-drop payload) and return every
 * `File` it contains, preserving the directory structure via the entries
 * API. This is the only way in a browser to read a dropped folder.
 */
export async function collectFilesFromDataTransfer(
  items: DataTransferItemList,
): Promise<File[]> {
  const files: File[] = [];
  const tasks: Promise<void>[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind !== "file") continue;
    const entry =
      (item as DataTransferItem & { webkitGetAsEntry?: () => FileSystemEntry | null }).webkitGetAsEntry?.() ??
      null;
    if (entry) {
      tasks.push(walkEntry(entry, "", files));
    } else {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }
  await Promise.all(tasks);
  return files;
}

async function walkEntry(
  entry: FileSystemEntry,
  prefix: string,
  collector: File[],
): Promise<void> {
  if ((entry as FileSystemFileEntry).isFile) {
    return new Promise((resolve, reject) => {
      (entry as FileSystemFileEntry).file(
        (file) => {
          const relPath = prefix ? `${prefix}/${file.name}` : file.name;
          // Rebrand the file so the relative path is preserved without
          // creating a synthetic File (which would lose the content stream).
          Object.defineProperty(file, "webkitRelativePath", {
            value: relPath,
            configurable: true,
          });
          collector.push(file);
          resolve();
        },
        reject,
      );
    });
  }
  const dirEntry = entry as FileSystemDirectoryEntry;
  const reader = dirEntry.createReader();
  const children = await readAllEntries(reader);
  const childPrefix = prefix ? `${prefix}/${dirEntry.name}` : dirEntry.name;
  for (const child of children) {
    await walkEntry(child, childPrefix, collector);
  }
}

function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const all: FileSystemEntry[] = [];
    const readBatch = () => {
      reader.readEntries((batch) => {
        if (!batch.length) {
          resolve(all);
          return;
        }
        all.push(...batch);
        readBatch();
      }, reject);
    };
    readBatch();
  });
}
